import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import omit from 'lodash.omit';

import {
  buildService,
  buildFacility,
  buildProductLine,
} from '../state';

import { snakeCaseKeys } from '../utils/shipments';
import { transformShipPoint } from '../utils/ship_point';

export const api = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({ baseUrl: '/api' }),
  endpoints: (builder) => ({
    cutOffTimes: builder.mutation({
      query: ({
        countryCode,
        state,
        city,
        zip,
        facilityId,
        dropOff,
      }) => {
        const body = {
          club_id: facilityId,
          drop_off: dropOff.toString(),
          country_code: countryCode,
          state,
          city,
          zip,
        };

        return {
          url: '/cut_off_times',
          method: 'POST',
          body,
        };
      },
    }),
  }),
});

export const v5Api = createApi({
  reducerPath: 'v5Api',
  baseQuery: fetchBaseQuery({ baseUrl: '/api/v5' }),
  endpoints: (builder) => ({
    packaging: builder.query({
      query: ({ productLineId }) => ({
        url: '/packaging',
        params: {
          product_line_id: productLineId,
        },
      }),
      transformResponse: (response) => response.packaging,
    }),
    insurance: builder.query({
      query: ({
        authToken,
        productLineId,
        international,
        orderId,
        countryCode,
        microSiteId,
      }) => ({
        url: '/products/insurances',
        headers: {
          'X-User-Token': authToken,
        },
        params: {
          product_line_id: productLineId,
          public: true,
          active: true,
          international: !!international,
          order_id: orderId,
          country_code: countryCode,
          micro_site_id: microSiteId,
        },
      }),
      transformResponse: (response) => response.products,
    }),
    products: builder.query({
      query: ({ productLineId, international, authToken }) => ({
        url: '/products',
        headers: {
          'X-User-Token': authToken,
        },
        params: {
          product_line_id: productLineId,
          product_type: 'label',
          public: true,
          international: !!international,
          active: true,
          allow_admin_only: true,
        },
      }),
      transformResponse: (response) => response.products,
    }),
    pickupFees: builder.query({
      query: ({
        brandId,
        userId,
        sameday,
        orderId,
        legId,
        facilityId,
        carrier,
        direction,
        microSiteId,
      }) => ({
        url: '/products/pickup_fee',
        params: {
          brand_id: brandId,
          user_id: userId,
          leg_id: legId,
          order_id: orderId,
          facility_id: facilityId,
          carrier,
          sameday,
          direction,
          micro_site_id: microSiteId,
        },
      }),
      transformResponse: (response) => response.pickup_fees,
    }),
    productLines: builder.query({
      query: ({ brandId, countryCode, authToken }) => ({
        url: '/product_lines',
        headers: {
          'X-User-Token': authToken,
        },
        params: {
          brand_id: brandId,
          country_code: countryCode,
        },
      }),
      transformResponse: (response) => response.product_lines.map(
        (productLine) => buildProductLine(productLine),
      ),
    }),
    countries: builder.query({
      query: (brandId) => ({
        url: '/countries',
        params: {
          brand_id: brandId,
        },
      }),
    }),
    country: builder.query({
      query: ({ countryCode, brandId }) => ({
        url: `/countries/${countryCode}`,
        params: {
          brand_id: brandId,
        },
      }),
    }),
    countryProvinces: builder.query({
      query: (countryCode) => ({
        url: `/countries/${countryCode}/provinces`,
      }),
    }),
    countryNotes: builder.query({
      query: (countryCode) => ({
        url: `/countries/${countryCode}/notes`,
      }),
    }),
    serviceOptions: builder.mutation({
      query: (data) => {
        const { authToken, body } = data;

        return {
          url: '/service_options',
          method: 'POST',
          headers: {
            'X-User-Token': authToken,
          },
          body: { ...body, combine: true },
        };
      },
      transformResponse: (response) => response.map(
        (service) => buildService(service),
      ),
    }),
    customerNotes: builder.query({
      query: ({ facilityId, direction }) => {
        const params = {};

        if (direction) {
          params.direction = direction;
        }

        return {
          url: `/notes/${facilityId}/consumer`,
          params,
        };
      },
    }),
    brandAlerts: builder.query({
      query: (brandId) => ({
        url: '/alerts',
        params: {
          brand_id: brandId,
        },
      }),
    }),
    disasterNotifications: builder.query({
      query: ({
        brandId,
        city,
        province,
        postalCode,
        countryCode,
        direction,
      }) => ({
        url: '/disaster_notifications',
        params: {
          brand_id: brandId,
          city,
          state_code: province,
          zip_code: postalCode,
          country_code: countryCode,
          address_type: direction,
        },
      }),
    }),
    giftCard: builder.query({
      query: (code) => ({
        url: `/gift_cards/${code}`,
      }),
    }),
    facilities: builder.query({
      query: ({ authToken }) => ({
        url: '/admin/facilities',
        headers: {
          'X-User-Token': authToken,
        },
      }),
    }),
    placeDetails: builder.mutation({
      query: ({ id }) => ({
        url: `/auto-complete/place-details/${id}`,
      }),
    }),
    eircodeDetails: builder.mutation({
      query: ({ suggestionId, query, countryCode }) => ({
        url: '/auto-complete/eircode-details',
        method: 'POST',
        body: {
          query,
          suggestion_id: suggestionId,
          country_code: countryCode,
        },
      }),
    }),
  }),
});

export const v5x1Api = createApi({
  reducerPath: 'v5x1Api',
  baseQuery: fetchBaseQuery({ baseUrl: '/api/v5.1' }),
  endpoints: (builder) => ({
    facility: builder.query({
      query: (id) => ({
        url: `/facilities/${id}`,
      }),
      transformResponse: (response) => buildFacility(response),
    }),
    unavailableDays: builder.query({
      query: ({
        brandId,
        countryCode,
        facilityId,
        dropOff,
        destinationCountryCode,
        destinationFacilityId,
      }) => {
        const params = {
          brand: brandId,
          dropoff: dropOff,
          country_code: countryCode,
        };

        if (facilityId) {
          params.facility_id = facilityId;
        }

        if (destinationCountryCode) {
          params.destination_country_code = destinationCountryCode;
        }

        if (destinationFacilityId) {
          params.destination_facility_id = destinationFacilityId;
        }

        return {
          url: '/calendar/unavailable_days',
          params,
        };
      },
    }),
    holidays: builder.query({
      query: ({ brandId, countryCode, destinationCountryCode }) => ({
        url: '/calendar/holidays',
        params: {
          brand_id: brandId,
          country_code: countryCode,
          destination_country_code: destinationCountryCode,
        },
      }),
    }),
    blockedAddresses: builder.mutation({
      query: (data) => {
        const {
          address1,
          address2,
          postalCode,
          city,
          state,
          countryCode,
        } = data;

        return {
          url: '/blocked_addresses/search',
          method: 'POST',
          body: {
            blocked_address: {
              address: address1,
              address_2: address2,
              zip: postalCode,
              city,
              state,
              country_code: countryCode,
            },
          },
        };
      },
    }),
    nextDay: builder.query({
      query: ({
        brandId,
        originFacilityId,
        originCountryCode,
        destinationFacilityId,
        destinationCountryCode,
        shipDate,
        carrierServiceLevelId,
        transitDays,
      }) => {
        const body = {
          brand: brandId,
          ship_date: shipDate,
          carrier_service_level_id: carrierServiceLevelId,
          transit_days: transitDays,
          origin: {
            country_code: originCountryCode,
          },
          destination: {
            country_code: destinationCountryCode,
          },
        };

        if (originFacilityId) {
          body.origin.facility_id = originFacilityId;
        }

        if (destinationFacilityId) {
          body.destination.facility_id = destinationFacilityId;
        }

        return {
          url: '/calendar/next_day',
          method: 'POST',
          body,
        };
      },
    }),
    autoComplete: builder.mutation({
      query: ({
        authToken,
        endpoint,
        term,
        brandId,
        currentUserId,
        countryCode,
      }) => ({
        url: `/auto-complete/${endpoint}`,
        method: 'GET',
        headers: {
          'X-User-Token': authToken,
        },
        params: {
          term,
          brand: brandId,
          user_id: currentUserId,
          country_code: countryCode,
        },
      }),
    }),
  }),
});

export const v5x2Api = createApi({
  reducerPath: 'v5x2Api',
  baseQuery: fetchBaseQuery({ baseUrl: '/api/v5.2' }),
  endpoints: (builder) => ({
    cartReceipt: builder.query({
      query: ({ orderId, groupBy, paymentMethod }) => {
        const params = {};

        if (groupBy) {
          params.group_by = groupBy;
        }

        if (paymentMethod) {
          params.payment_method = paymentMethod;
        }

        return {
          url: `/carts/${orderId}/receipt`,
          params,
        };
      },
    }),
    updateCartItems: builder.mutation({
      query: (data) => {
        const {
          cartId,
          newShipments,
          feeId,
          legId,
        } = data;

        const shipments = newShipments.map(
          (shipment) => omit(shipment, ['productLineName']),
        );

        return {
          url: `/carts/${cartId}/items`,
          method: 'PUT',
          body: {
            cart_item: {
              shipments: snakeCaseKeys(shipments),
              fee_id: feeId,
              leg_id: legId,
            },
          },
        };
      },
    }),
    createOrUpdateCartItems: builder.mutation({
      query: (data) => {
        const { cartId } = data;

        const shipments = data.shipments.map(
          (shipment) => omit(shipment, ['productLineName']),
        );

        return {
          url: `/carts/${cartId}/items`,
          method: 'POST',
          body: {
            cart_item: {
              shipments: snakeCaseKeys(shipments),
            },
          },
        };
      },
    }),
    createOrder: builder.mutation({
      query: (data) => {
        const {
          brandId,
          authToken,
          orderId,
          cart: {
            id: cartId,
            token: cartToken,
          },
          payment: {
            payerName,
            payerEmail,
            paymentMethodId,
            billingZipCode,
            billingCountry,
          },
          giftCardIds,
        } = data;

        const body = {
          cart_id: cartId,
          cart_token: cartToken,
          brand_id: brandId,
          payment: {
            payment_method_id: paymentMethodId,
            payer_name: payerName,
            payer_email: payerEmail,
            gift_card_ids: giftCardIds,
            billing_zip_code: billingZipCode,
            billing_country: billingCountry,
          },
        };

        return {
          url: `/orders/${orderId}`,
          method: 'PUT',
          headers: {
            'X-User-Token': authToken,
            'X-Cart-Token': cartToken,
          },
          body,
        };
      },
    }),
    destroyCart: builder.mutation({
      query: (data) => {
        const {
          authToken,
          orderId,
          cartToken,
        } = data;

        return {
          url: `/carts/${orderId}`,
          method: 'DELETE',
          headers: {
            'X-Cart-Token': cartToken,
            'X-User-Token': authToken,
          },
        };
      },
    }),
    createLeg: builder.mutation({
      query: ({
        cartToken,
        authToken,
        legId,
        inverse,
      }) => ({
        url: `/legs/${legId}/clone`,
        method: 'POST',
        headers: {
          'X-Cart-Token': cartToken,
          'X-User-Token': authToken,
        },
        body: {
          leg: {
            inverse,
          },
        },
      }),
    }),
    updateLeg: builder.mutation({
      query: (data) => {
        const {
          cartToken,
          authToken,
          legId,
          method,
          feeId,
        } = data;

        return {
          url: `/legs/${legId}`,
          method: 'PUT',
          headers: {
            'X-Cart-Token': cartToken,
            'X-User-Token': authToken,
          },
          body: {
            leg: {
              method: method === 'dropOff' ? 'drop_off' : 'pickup',
              fee_id: feeId,
            },
          },
        };
      },
    }),
    replaceLeg: builder.mutation({
      query: (data) => {
        const {
          cartToken,
          authToken,
          legId,
          newLeg,
          shipments,
          feeId,
        } = data;

        const {
          id,
          sourceId,
          origin,
          destination,
        } = newLeg;

        return {
          url: `/legs/${legId}/replace`,
          method: 'POST',
          headers: {
            'X-Cart-Token': cartToken,
            'X-User-Token': authToken,
          },
          body: {
            leg: {
              new_leg: {
                id,
                source_id: sourceId,
                origin: transformShipPoint(origin),
                destination: transformShipPoint(destination),
              },
              shipments: snakeCaseKeys(shipments),
              fee_id: feeId,
            },
          },
        };
      },
    }),
    cartGiftCards: builder.query({
      query: ({ orderId }) => ({
        url: `/carts/${orderId}/gift_cards`,
      }),
    }),
    addGiftCardToCart: builder.mutation({
      query: ({ id, orderId }) => ({
        url: `/carts/${orderId}/gift_cards`,
        method: 'POST',
        body: {
          gift_cards: { id },
        },
      }),
    }),
    removeGiftCardToCart: builder.mutation({
      query: ({ id, orderId }) => ({
        url: `/carts/${orderId}/gift_cards/${id}`,
        method: 'DELETE',
      }),
    }),
    createTraveler: builder.mutation({
      query: (data) => {
        const {
          authToken,
          name,
          email,
          phone,
          cartId,
          sourceId,
          legId,
        } = data;

        const traveler = {
          name,
          email,
          phone,
        };

        if (sourceId) {
          traveler.source_id = sourceId;
        }

        if (legId) {
          traveler.leg_id = legId;
        }

        return {
          url: `/carts/${cartId}/travelers`,
          method: 'POST',
          headers: {
            'X-User-Token': authToken,
          },
          body: { traveler },
        };
      },
    }),
    updateTraveler: builder.mutation({
      query: (data) => {
        const {
          authToken,
          cartId,
          name,
          email,
          phone,
          id,
          state,
        } = data;

        return {
          url: `/carts/${cartId}/travelers/${id}`,
          method: 'PUT',
          headers: {
            'X-User-Token': authToken,
          },
          body: {
            traveler: {
              name,
              email,
              phone,
              state,
            },
          },
        };
      },
    }),
    setWatchedContent: builder.mutation({
      query: (data) => {
        const {
          authToken,
          orderId,
          category,
        } = data;

        const userId = window.user_ident;

        return {
          url: `/carts/${orderId}/watched/${userId}`,
          method: 'PUT',
          headers: {
            'X-User-Token': authToken,
          },
          body: {
            watched: {
              category,
            },
          },
        };
      },
    }),
    destroyLeg: builder.mutation({
      query: ({ legId, cartToken, authToken }) => ({
        url: `/legs/${legId}`,
        method: 'DELETE',
        headers: {
          'X-Cart-Token': cartToken,
          'X-User-Token': authToken,
        },
      }),
    }),
    cartAlerts: builder.query({
      query: ({ orderId }) => ({
        url: `/carts/${orderId}/alerts`,
      }),
    }),
    destroyOrder: builder.mutation({
      query: ({ orderId, cartToken, authToken }) => ({
        url: `/orders/${orderId}`,
        method: 'DELETE',
        headers: {
          'X-Cart-Token': cartToken,
          'X-User-Token': authToken,
        },
      }),
    }),
    autoComplete: builder.mutation({
      query: ({
        authToken,
        endpoint,
        term,
        brandId,
        currentUserId,
        countryCode,
      }) => ({
        url: `/auto-complete/${endpoint}`,
        method: 'GET',
        headers: {
          'X-User-Token': authToken,
        },
        params: {
          term,
          brand: brandId,
          user_id: currentUserId,
          country_code: countryCode,
        },
      }),
    }),
  }),
});

export const {
  useCutOffTimesMutation,
} = api;

export const {
  usePackagingQuery,
  useInsuranceQuery,
  useProductsQuery,
  usePickupFeesQuery,
  useProductLinesQuery,
  useCountriesQuery,
  useCountryQuery,
  useCountryProvincesQuery,
  useCountryNotesQuery,
  useServiceOptionsMutation,
  useCustomerNotesQuery,
  useBrandAlertsQuery,
  useDisasterNotificationsQuery,
  useGiftCardQuery,
  useFacilitiesQuery,
  usePlaceDetailsMutation,
  useEircodeDetailsMutation,
} = v5Api;

export const {
  useFacilityQuery,
  useUnavailableDaysQuery,
  useLazyHolidaysQuery,
  useBlockedAddressesMutation,
  useNextDayQuery,
  useAutoCompleteMutation,
} = v5x1Api;

export const {
  useUpdateCartItemsMutation,
  useCreateOrUpdateCartItemsMutation,
  useCartReceiptQuery,
  useCreateOrderMutation,
  useDestroyCartMutation,
  useCartGiftCardsQuery,
  useAddGiftCardToCartMutation,
  useRemoveGiftCardToCartMutation,
  useCreateLegMutation,
  useCreateTravelerMutation,
  useUpdateTravelerMutation,
  useDestroyLegMutation,
  useUpdateLegMutation,
  useReplaceLegMutation,
  useCartAlertsQuery,
  useDestroyOrderMutation,
  useSetWatchedContentMutation,
  useAutoCompleteMutation: use5x2AutoCompleteMutation,
} = v5x2Api;

export const v6TravelAgentApi = createApi({
  reducerPath: 'v6TravelAgentApi',
  baseQuery: fetchBaseQuery({ baseUrl: '/api/v6/travel_agent' }),
  endpoints: (builder) => ({
    consortiums: builder.query({
      query: () => '/consortiums',
      transformResponse: (response) => response.data,
    }),
    agencies: builder.query({
      query: (consortiumId) => `/agencies?consortium_id=${consortiumId}`,
      transformResponse: (response) => response.data,
    }),
    subAgencies: builder.query({
      query: (parentAgencyId) => `/agencies?parent_agency_id=${parentAgencyId}`,
      transformResponse: (response) => response.data,
    }),
    agencyAutocomplete: builder.query({
      query: ({ consortiumId, parentAgencyId, term, limit }) => {
        const params = new URLSearchParams();
        if (consortiumId) params.set('consortium_id', consortiumId);
        if (parentAgencyId) params.set('parent_agency_id', parentAgencyId);
        if (term) params.set('term', term);
        if (limit) params.set('limit', limit);
        return `/agencies/autocomplete?${params.toString()}`;
      },
      transformResponse: (response) => response.data,
    }),
  }),
});

export const {
  useConsortiumsQuery,
  useAgenciesQuery,
  useSubAgenciesQuery,
  useLazyAgencyAutocompleteQuery,
} = v6TravelAgentApi;
